import Phaser from 'phaser';
import { setCharacterAnimation, setCharacterFacing, setFevziAction } from '../cafe/VisualFactory';
import type { HelperState } from '../types';
import { HelperStateMachine } from './HelperStateMachine';

export interface HelperTask {
  kind: 'PREPARING' | 'CLEANING';
  animation?: 'prepareCoffee' | 'prepareColdDrink' | 'cleanTable' | 'help';
  x: number;
  y: number;
  run(): void;
}

export class FevziHelperSystem {
  readonly stateMachine = new HelperStateMachine();
  actionCount = 0;
  enabled: boolean;
  private cooldownRemaining = 2.5;
  private readonly home = { x: 1200, y: 231 };

  constructor(
    private readonly scene: Phaser.Scene,
    private readonly visual: Phaser.GameObjects.Container,
    enabled: boolean,
    private readonly speedMultiplier: number,
    private readonly cooldown: number,
    private readonly onAction: (state: HelperState) => void,
  ) {
    this.enabled = enabled;
  }

  update(delta: number, getTask: () => HelperTask | undefined): void {
    if (!this.enabled || this.stateMachine.state !== 'IDLE') return;
    this.cooldownRemaining -= delta;
    if (this.cooldownRemaining > 0) return;
    const task = getTask();
    if (!task) return;
    this.startTask(task);
  }

  trigger(getTask: () => HelperTask | undefined): boolean {
    if (!this.enabled || this.stateMachine.state !== 'IDLE') return false;
    const task = getTask();
    if (!task) return false;
    this.startTask(task);
    return true;
  }

  private startTask(task: HelperTask): void {
    this.stateMachine.transition('MOVING');
    setFevziAction(this.visual, task.kind === 'CLEANING' ? 'MASAYA GİDİYOR' : 'HAZIRLIYOR');
    setCharacterFacing(this.visual, task.x < this.visual.x ? -1 : 1);
    setCharacterAnimation(this.visual, 'walk');
    this.visual.setDepth(34);
    const distance = Phaser.Math.Distance.Between(this.visual.x, this.visual.y, task.x, task.y);
    this.scene.tweens.add({
      targets: this.visual,
      x: task.x,
      y: task.y,
      duration: Math.max(450, distance / (0.34 * this.speedMultiplier)),
      ease: 'Sine.inOut',
      onComplete: () => {
        this.stateMachine.transition(task.kind);
        setFevziAction(this.visual, task.kind === 'CLEANING' ? 'TEMİZLİYOR' : 'DESTEK OLUYOR');
        setCharacterAnimation(this.visual, task.animation ?? (task.kind === 'CLEANING' ? 'cleanTable' : 'help'));
        this.onAction(task.kind);
        this.scene.time.delayedCall(task.kind === 'CLEANING' ? 950 : 700, () => {
          task.run();
          this.actionCount += 1;
          this.returnHome();
        });
      },
    });
  }

  private returnHome(): void {
    this.stateMachine.transition('RETURNING');
    setFevziAction(this.visual, 'YERİNE DÖNÜYOR');
    setCharacterFacing(this.visual, 1);
    setCharacterAnimation(this.visual, 'returnWalk');
    this.scene.tweens.add({
      targets: this.visual,
      x: this.home.x,
      y: this.home.y,
      duration: 900 / this.speedMultiplier,
      ease: 'Sine.inOut',
      onComplete: () => {
        this.visual.setDepth(18);
        this.stateMachine.transition('IDLE');
        setCharacterAnimation(this.visual, 'idleBehindCounter', 'left');
        setFevziAction(this.visual, '');
        this.cooldownRemaining = this.cooldown;
      },
    });
  }
}
